import React from "react";
import { Button } from "@/components/ui/button";
import { Phone, MessageSquare, Mail, MessageCircle } from "lucide-react";

interface LeadQuickActionsProps {
  phone: string;
  email?: string;
  name: string;
  service?: string;
  compact?: boolean;
  partnerName?: string;
}

// Normalize phone for tel: / sms: / WhatsApp links
const normalizePhone = (p: string): string => {
  if (!p) return "";
  const clean = p.replace(/[-\s()]/g, "");
  if (clean.startsWith("+")) return clean;
  if (clean.startsWith("00")) return `+${clean.slice(2)}`;
  if (clean.startsWith("30") && clean.length >= 12) return `+${clean}`;
  if (clean.startsWith("6") || clean.startsWith("2")) return `+30${clean}`;
  return clean;
};

// Get WhatsApp URL (strip + for wa.me)
const getWhatsAppUrl = (normalizedPhone: string, message: string): string => {
  const phoneDigits = normalizedPhone.replace(/\+/g, "");
  return `https://wa.me/${phoneDigits}?text=${encodeURIComponent(message)}`;
};

const LeadQuickActions: React.FC<LeadQuickActionsProps> = ({
  phone,
  email,
  name,
  service,
  compact = false,
  partnerName,
}) => {
  const normalizedPhone = normalizePhone(phone);

  // Pre-filled message templates
  const firstName = name?.split(" ")[0] || "";
  const greeting = `Γεια σας ${firstName}`;
  const intro = partnerName ? ` Είμαι από την ${partnerName}.` : "";
  const context = service
    ? ` Επικοινωνώ σχετικά με το αίτημά σας για ${service}.`
    : " Επικοινωνώ σχετικά με το αίτημά σας.";

  const whatsappMessage = `${greeting},${intro}${context}`;
  const smsMessage = `${greeting},${intro}${context}`;
  const emailSubject = service
    ? `Σχετικά με το αίτημα για ${service}`
    : "Σχετικά με το αίτημά σας";
  const emailBody = `${greeting},${intro}${context}\n\nΠαρακαλώ ενημερώστε με για την καλύτερη ώρα επικοινωνίας.\n\nΕυχαριστώ!`;

  const telUrl = `tel:${normalizedPhone}`;
  const whatsappUrl = getWhatsAppUrl(normalizedPhone, whatsappMessage);
  const smsUrl = `sms:${normalizedPhone}?body=${encodeURIComponent(smsMessage)}`;
  const emailUrl = email
    ? `mailto:${email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`
    : undefined;

  const stopProp = (e: React.MouseEvent) => e.stopPropagation();

  if (compact) {
    return (
      <div className="flex items-center gap-2" onClick={stopProp} aria-label="Γρήγορη επικοινωνία">
        <a
          href={telUrl}
          onClick={stopProp}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
          title="Κλήση"
          aria-label={`Κλήση στον ${name}`}
        >
          <Phone className="h-[18px] w-[18px]" />
        </a>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stopProp}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#25D366] text-white transition-colors hover:bg-[#128C7E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2"
          title="WhatsApp"
          aria-label={`WhatsApp στον ${name}`}
        >
          <MessageCircle className="h-[18px] w-[18px]" />
        </a>
        <a
          href={smsUrl}
          onClick={stopProp}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-600 text-white transition-colors hover:bg-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
          title="SMS"
          aria-label={`SMS στον ${name}`}
        >
          <MessageSquare className="h-[18px] w-[18px]" />
        </a>
        {email && emailUrl && (
          <a
            href={emailUrl}
            onClick={stopProp}
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-700 text-white transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
            title="Email"
            aria-label={`Email στον ${name}`}
          >
            <Mail className="h-[18px] w-[18px]" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" onClick={stopProp} aria-label="Γρήγορη επικοινωνία">
      <Button
        asChild
        size="sm"
        className="h-11 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
      >
        <a href={telUrl} onClick={stopProp}>
          <Phone className="h-4 w-4 mr-1.5" />
          Κλήση
        </a>
      </Button>
      <Button
        asChild
        size="sm"
        className="h-11 rounded-xl bg-[#25D366] text-white hover:bg-[#128C7E]"
      >
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stopProp}
        >
          <MessageCircle className="h-4 w-4 mr-1.5" />
          WhatsApp
        </a>
      </Button>
      <Button asChild size="sm" variant="outline" className="h-11 rounded-xl">
        <a href={smsUrl} onClick={stopProp}>
          <MessageSquare className="h-4 w-4 mr-1.5" />
          SMS
        </a>
      </Button>
      {email && emailUrl && (
        <Button asChild size="sm" variant="outline" className="h-11 rounded-xl">
          <a href={emailUrl} onClick={stopProp}>
            <Mail className="h-4 w-4 mr-1.5" />
            Email
          </a>
        </Button>
      )}
    </div>
  );
};

export default LeadQuickActions;
